import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { watchlistAPI } from '../services/api';
import { HiOutlineFilm, HiCheck, HiOutlineTrash, HiOutlineX, HiOutlineSearch, HiOutlineShare } from 'react-icons/hi';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { StarRating } from '../components';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w200';

function Movies() {
  const { t } = useTranslation();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [dateSortDirection, setDateSortDirection] = useState('desc'); // 'desc' = mais recente, 'asc' = mais antiga
  const [alphabeticalSortDirection, setAlphabeticalSortDirection] = useState('asc'); // 'asc' = A-Z, 'desc' = Z-A
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    movieId: null,
    event: null,
  });

  useEffect(() => {
    loadMovies();
  }, []);

  const loadMovies = async () => {
    setLoading(true);
    try {
      const response = await watchlistAPI.getMovies();
      setMovies(response.data);
    } catch (error) {
      console.error('Error loading movies:', error);
      toast.error(t('movies.errorLoadingMovies'));
    } finally {
      setLoading(false);
    }
  };

  const toggleWatched = async (movie) => {
    try {
      await watchlistAPI.update(movie.id, { watched: !movie.watched });
      setMovies((prev) =>
        prev.map((m) =>
          m.id === movie.id ? { ...m, watched: !m.watched } : m
        )
      );
      toast.success(movie.watched ? t('watchlist.markedAsUnwatched') : t('watchlist.markedAsWatched'));
    } catch (error) {
      toast.error(t('movies.errorUpdating'));
    }
  };

  const handleRemoveClick = (id, e) => {
    e.stopPropagation();
    setConfirmModal({ isOpen: true, movieId: id, event: e });
  };

  const removeMovie = async () => {
    if (!confirmModal.movieId) return;
    
    try {
      await watchlistAPI.remove(confirmModal.movieId);
      setMovies((prev) => prev.filter((m) => m.id !== confirmModal.movieId));
      toast.success(t('movies.movieRemoved'));
      setConfirmModal({ isOpen: false, movieId: null, event: null });
    } catch (error) {
      toast.error(t('movies.errorRemoving'));
    }
  };

  const handleRate = async (movieId, rating) => {
    try {
      await watchlistAPI.update(movieId, { user_rating: rating });
      setMovies(prev => prev.map(m =>
        m.id === movieId ? { ...m, user_rating: rating } : m
      ));
      toast.success(rating > 0 ? t('rating.saved') : t('rating.removed'));
    } catch (error) {
      toast.error(t('rating.error'));
    }
  };

  const handleDateSort = () => {
    if (sortBy === 'date') {
      // Toggle direction if already sorting by date
      setDateSortDirection(dateSortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      // Switch to date sort with default direction
      setSortBy('date');
      setDateSortDirection('desc');
    }
  };

  const handleAlphabeticalSort = () => {
    if (sortBy === 'alphabetical') {
      setAlphabeticalSortDirection(alphabeticalSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy('alphabetical');
      setAlphabeticalSortDirection('asc');
    }
  };

  const filteredMovies = movies.filter((m) => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'watched' && m.watched) ||
      (filter === 'unwatched' && !m.watched);

    const matchesSearch =
      searchQuery.trim() === '' ||
      m.title.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const clearSearch = () => {
    setSearchQuery('');
  };

  // Sort movies based on sortBy state
  const sortedMovies = [...filteredMovies].sort((a, b) => {
    if (sortBy === 'date') {
      // Sort by added_at
      const dateA = new Date(a.added_at || 0);
      const dateB = new Date(b.added_at || 0);
      return dateSortDirection === 'desc' ? dateB - dateA : dateA - dateB;
    } else if (sortBy === 'alphabetical') {
      // Sort alphabetically by title
      const result = a.title.localeCompare(b.title, 'pt-BR');
      return alphabeticalSortDirection === 'asc' ? result : -result;
    }
    return 0;
  });

  const watchedCount = movies.filter((m) => m.watched).length;

  const handleShareList = async () => {
    const rated = movies.filter(m => m.user_rating > 0).sort((a, b) => b.user_rating - a.user_rating);
    const watched = movies.filter(m => m.watched && !m.user_rating);
    const unwatched = movies.filter(m => !m.watched);
    const getYear = (m) => m.release_date ? new Date(m.release_date).getFullYear() : '';

    let text = `🎬 ${t('movies.myMovies')} — My Series\n`;
    text += `📊 ${movies.length} ${t('share.moviesInList')} • ${watchedCount} ${t('share.watchedLabel')}\n`;

    if (rated.length > 0) {
      text += `\n⭐ ${t('share.myRatings')}:\n`;
      rated.forEach(m => {
        const year = getYear(m);
        text += `★ ${m.user_rating} — ${m.title}${year ? ` (${year})` : ''}\n`;
      });
    }
    if (watched.length > 0) {
      text += `\n✅ ${t('share.watched')}:\n`;
      watched.forEach(m => {
        const year = getYear(m);
        text += `• ${m.title}${year ? ` (${year})` : ''}\n`;
      });
    }
    if (unwatched.length > 0) {
      text += `\n🎯 ${t('share.wantToWatch')}:\n`;
      unwatched.forEach(m => {
        const year = getYear(m);
        text += `• ${m.title}${year ? ` (${year})` : ''}\n`;
      });
    }

    const copyToClipboard = (value) => {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(t('share.copied'));
    };

    try {
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (navigator.share && isMobile) {
        await navigator.share({ title: t('movies.myMovies'), text });
      } else if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(t('share.copied'));
      } else {
        copyToClipboard(text);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          copyToClipboard(text);
        } catch {
          toast.error(t('share.error'));
        }
      }
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t('movies.myMovies')}</h1>
        <p>
          {movies.length} {t('movies.moviesInList')} • {watchedCount} {t('movies.watched')}
        </p>
        <button
          className="sort-btn"
          onClick={handleShareList}
          title={t('share.shareList')}
          style={{ marginTop: '8px' }}
        >
          <HiOutlineShare size={16} />
          {t('share.shareList')}
        </button>
        {/* Mobile sort indicator */}
        <div className="mobile-sort-indicator">
          {sortBy === 'date' && (
            <span>
              📅 {dateSortDirection === 'desc' ? t('movies.newest') : t('movies.oldest')}
            </span>
          )}
          {sortBy === 'alphabetical' && (
            <span>
              🔤 {alphabeticalSortDirection === 'asc' ? 'A-Z' : 'Z-A'}
            </span>
          )}
        </div>
      </div>

      <div className="tabs tabs--filter">
        <button
          className={`tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          {t('movies.all')} ({movies.length})
        </button>
        <button
          className={`tab ${filter === 'unwatched' ? 'active' : ''}`}
          onClick={() => setFilter('unwatched')}
        >
          {t('movies.unwatched')} ({movies.length - watchedCount})
        </button>
        <button
          className={`tab ${filter === 'watched' ? 'active' : ''}`}
          onClick={() => setFilter('watched')}
        >
          {t('watchlist.watched')} ({watchedCount})
        </button>
      </div>

      <div className="sort-controls">
        <div className="sort-buttons">
          <span className="sort-label">{t('movies.sortBy')}:</span>
          <button
            className={`sort-btn ${sortBy === 'date' ? 'active' : ''}`}
            onClick={handleDateSort}
          >
            <span className="sort-icon">📅</span>
            {t('movies.dateAdded')}
          </button>
          <button
            className={`sort-btn ${sortBy === 'alphabetical' ? 'active' : ''}`}
            onClick={handleAlphabeticalSort}
          >
            <span className="sort-icon">🔤</span>
            {t('movies.alphabeticalOrder')}
          </button>
          <div className="search-container" style={{ marginBottom: 0, maxWidth: '300px', height: '38px' }}>
            <HiOutlineSearch className="search-icon" style={{ width: '18px', height: '18px' }} />
            <input
              type="text"
              className="search-input"
              placeholder={t('movies.searchMovie')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ height: '38px', minHeight: '38px', padding: '0 40px 0 40px' }}
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={clearSearch}
                aria-label="Clear search"
                style={{ width: '20px', height: '20px', right: '10px' }}
              >
                <HiOutlineX size={14} />
              </button>
            )}
          </div>
        </div>
        {sortBy === 'date' && (
          <div className="sort-indicator">
            <span className="sort-direction">
              {dateSortDirection === 'desc' ? '⬇️' : '⬆️'}
            </span>
            <span className="sort-text">
              {dateSortDirection === 'desc' ? t('movies.newest') : t('movies.oldest')}
            </span>
          </div>
        )}
        {sortBy === 'alphabetical' && (
          <div className="sort-indicator">
            <span className="sort-direction">
              {alphabeticalSortDirection === 'asc' ? '🔤' : '🔠'}
            </span>
            <span className="sort-text">
              {alphabeticalSortDirection === 'asc' ? 'A-Z' : 'Z-A'}
            </span>
          </div>
        )}
      </div>

      <div className="movies-list-scrollable">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : sortedMovies.length > 0 ? (
          <div>
            {sortedMovies.map((movie) => (
              <div key={movie.id} className="watchlist-item">
                <div className="watchlist-item-poster">
                  {movie.poster_path ? (
                    <img
                      src={`${TMDB_IMAGE_BASE}${movie.poster_path}`}
                      alt={movie.title}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        background: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <HiOutlineFilm size={24} color="#9ca3af" />
                    </div>
                  )}
                </div>
                <div className="watchlist-item-content">
                  <h3 className="watchlist-item-title">{movie.title}</h3>
                  <p className="watchlist-item-meta">
                    {movie.release_date
                      ? new Date(movie.release_date).getFullYear()
                      : t('movies.unknownYear')}
                    {movie.vote_average > 0 && ` • ⭐ ${movie.vote_average.toFixed(1)}`}
                  </p>
                  <div className="star-rating-with-value">
                    <StarRating
                      rating={movie.user_rating || 0}
                      onRate={(rating) => handleRate(movie.id, rating)}
                      size={18}
                    />
                    {movie.user_rating > 0 && (
                      <span className="star-rating-value">{movie.user_rating} / 5</span>
                    )}
                  </div>
                  <div className="watchlist-item-actions">
                    <button
                      className={`btn ${movie.watched ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => toggleWatched(movie)}
                    >
                      <HiCheck />
                      {movie.watched ? t('watchlist.watched') : t('watchlist.markAsWatchedShort')}
                    </button>
                    <button
                      className="btn btn-icon"
                      onClick={(e) => handleRemoveClick(movie.id, e)}
                      title={t('movies.remove')}
                    >
                      <HiOutlineTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <HiOutlineFilm size={32} />
            </div>
            <h3>{t('movies.noMoviesFound')}</h3>
            <p>
              {filter !== 'all'
                ? t('movies.noMoviesWithFilter')
                : t('movies.addMoviesFromSearch')}
            </p>
          </div>
        )}
      </div>

      {/* Confirm Remove Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={t('movies.removeMovie')}
        message={t('movies.confirmRemove')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={removeMovie}
        onCancel={() => setConfirmModal({ isOpen: false, movieId: null, event: null })}
        type="danger"
      />
    </div>
  );
}

export default Movies;
